import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Stack, TextInput, Group, LoadingOverlay, Container, Alert, Select } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IconAlertCircle } from '@tabler/icons-react';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import { masterUserAdminService } from 'services/dacum/masterUserAdminService';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { UpdateMasterUserAdminDto } from 'services/api';
import PageTitle from 'components/PageTitle';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import MultiAreaSelector from '../components/MultiAreaSelector';

const items = [
  { title: 'Master Admin User', href: '/app/dacum/master-user-admin' },
  { title: 'Edit Admin User', href: '' }
];

const DEPARTMENTS = [
  { value: 'Learning Solutions', label: 'Learning Solutions' },
  { value: 'Learning Operations', label: 'Learning Operations' },
  { value: 'Learning Partner', label: 'Learning Partner' },
  { value: 'Learning Technology and Experience', label: 'Learning Technology and Experience' }
];

const ROLES = [
  { value: 'Manager', label: 'Manager' },
  { value: 'General', label: 'General (Umum)' }
];

// Updated schema to handle array of area IDs
const userAdminSchema = z.object({
  adminId: z.string().min(1, 'Admin ID is required'),
  adminName: z.string().min(1, 'Admin name is required'),
  adminEmail: z.string().email('Valid email is required'),
  beelajarEmail: z.string().optional(),
  department: z.string().min(1, 'Department is required'),
  role: z.string().min(1, 'Role is required'),
  areaIds: z.array(z.string()).min(1, 'At least one area is required')
});

type FormData = z.infer<typeof userAdminSchema>;

const EditMasterUserAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: userAdminId } = useParams<{ id: string }>();
  const [opened, { open, close }] = useDisclosure();
  const [formData, setFormData] = useState<FormData | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(userAdminSchema),
    defaultValues: {
      adminId: '',
      adminName: '',
      adminEmail: '',
      beelajarEmail: '',
      department: '',
      role: '',
      areaIds: []
    }
  });

  const { data: areasData, isLoading: isLoadingAreas } = useQuery({
    queryKey: ['masterAreas'],
    queryFn: () => masterAreaService.getList({ limit: 100 }),
    select: (response) => ({
      ...response,
      data: response.data.map((area: any) => ({
        value: area.areaID,
        label: `${area.areaCode} - ${area.areaName}`
      }))
    })
  });

  const {
    data: userAdminData,
    isLoading: isLoadingUserAdmin,
    error: userAdminError
  } = useQuery({
    queryKey: ['masterUserAdminDetail', userAdminId],
    queryFn: () => masterUserAdminService.getDetail(userAdminId!),
    enabled: !!userAdminId && !isLoadingAreas,
    select: (response) => {
      return response.data || response;
    },
    retry: 1
  });

  useEffect(() => {
    if (userAdminData && areasData?.data) {
      // Handle both new format (listArea) and old format (areaId) for backward compatibility
      let areaIds: string[] = [];

      if (userAdminData.listArea && Array.isArray(userAdminData.listArea)) {
        // New format with multiple areas
        areaIds = userAdminData.listArea.map((area: any) => area.areaId || area.areaID);
      } else if (userAdminData.areaID || userAdminData.areaId) {
        // Old format with single area - convert to array
        areaIds = [userAdminData.areaID || userAdminData.areaId];
      }

      reset({
        adminId: userAdminData.adminID,
        adminName: userAdminData.adminName,
        adminEmail: userAdminData.adminEmail,
        beelajarEmail: userAdminData.beelajarEmail || '',
        department: userAdminData.department || '',
        role: userAdminData.role || '',
        areaIds: areaIds.filter(Boolean) // Remove any null/undefined values
      });
    }
  }, [userAdminData, areasData?.data, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => {
      if (!userAdminId) throw new Error('User Admin ID is required');

      const updateDto: UpdateMasterUserAdminDto = {
        msUserAdminId: userAdminId,
        adminId: data.adminId,
        adminName: data.adminName,
        adminEmail: data.adminEmail,
        beelajarEmail: data.beelajarEmail || undefined,
        department: data.department,
        role: data.role,
        listArea: data.areaIds.map((areaId) => ({ areaId })) // Convert to required format
      };

      return masterUserAdminService.update(updateDto);
    },
    onSuccess: () => {
      navigate('/app/dacum/master-user-admin');
      showSuccessNotification({
        title: 'Updated Successfully!',
        message: 'Admin user has been updated successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to update admin user',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const processSubmit = () => {
    if (formData) {
      updateMutation.mutate(formData);
    }
    close();
  };

  // Redirect if no ID provided
  useEffect(() => {
    if (!userAdminId) {
      navigate('/app/dacum/master-user-admin');
      showErrorNotification({
        title: 'Invalid Request',
        message: 'User Admin ID is required for editing'
      });
    }
  }, [userAdminId, navigate]);

  if (userAdminError) {
    return (
      <Container fluid>
        <PageTitle title='Edit Admin User' />
        <BreadcrumbsComponent items={items} />

        <Alert
          icon={<IconAlertCircle size='1rem' />}
          title='Error Loading Data'
          color='red'
          mt={20}
        >
          Unable to load admin user details. Please check if the ID is correct and try again.
        </Alert>

        <Group mt={20}>
          <SecondaryButton
            text='Back to List'
            onClick={() => navigate('/app/dacum/master-user-admin')}
          />
        </Group>
      </Container>
    );
  }

  if (!userAdminId) {
    return null;
  }

  return (
    <Container fluid>
      <PageTitle title='Edit Admin User' />
      <BreadcrumbsComponent items={items} />

      <ModalPopupConfirmationComponent
        buttonText='Update'
        statusProcess='edit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processSubmit}
        itemLabel='admin user'
      />

      <div style={{ marginTop: '20px', position: 'relative' }}>
        <LoadingOverlay
          visible={isLoadingUserAdmin || isLoadingAreas || updateMutation.isPending}
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap='md'>
            <Controller
              name='adminId'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Admin ID (Binusian ID)'
                  placeholder='Admin ID'
                  error={errors.adminId?.message}
                  required
                  readOnly
                  variant='filled'
                  description='This field cannot be edited'
                  styles={{
                    input: {
                      backgroundColor: '#f5f5f5',
                      color: '#333'
                    }
                  }}
                  {...field}
                />
              )}
            />

            <Controller
              name='adminName'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Admin Name'
                  placeholder='Enter admin name'
                  error={errors.adminName?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name='adminEmail'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Admin Email'
                  placeholder='Admin email'
                  error={errors.adminEmail?.message}
                  required
                  readOnly
                  variant='filled'
                  description='This field cannot be edited'
                  styles={{
                    input: {
                      backgroundColor: '#f5f5f5',
                      color: '#333'
                    }
                  }}
                  {...field}
                />
              )}
            />

            <Controller
              name='beelajarEmail'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Beelajar Email'
                  placeholder='Enter beelajar email (optional)'
                  error={errors.beelajarEmail?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='department'
              control={control}
              render={({ field }) => (
                <Select
                  label='Department'
                  placeholder='Select department'
                  data={DEPARTMENTS}
                  error={errors.department?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name='role'
              control={control}
              render={({ field }) => (
                <Select
                  label='Role'
                  placeholder='Select role'
                  data={ROLES}
                  error={errors.role?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name='areaIds'
              control={control}
              render={({ field }) => (
                <MultiAreaSelector
                  areas={areasData?.data || []}
                  value={field.value}
                  onChange={field.onChange}
                  loading={isLoadingAreas}
                  error={errors.areaIds?.message}
                  required
                />
              )}
            />
          </Stack>

          <Group justify='flex-end' mt='xl'>
            <SecondaryButton
              text='Cancel'
              onClick={() => navigate('/app/dacum/master-user-admin')}
              mr={10}
            />
            <PrimaryButton
              text='Update'
              type='submit'
              loading={isSubmitting || updateMutation.isPending}
            />
          </Group>
        </form>
      </div>
    </Container>
  );
};

export default EditMasterUserAdminPage;
